import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import authService from '../../services/authService';
import AuthLayout from '../../layouts/auth/authLayout';
import '../../styles/components/auth/auth_forms.css';

import eyeOpen from '../../assets/icons/indicators/eye_open_icon.svg';
import eyeClosed from '../../assets/icons/indicators/eye_closed_icon.svg'; 

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'doctor', // Changed from 'patient' to 'doctor'
    tenantId: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { 
    firstName, lastName, email, password, confirmPassword, tenantId
  } = formData;
  
  // Fetch available tenants on component mount
  useEffect(() => {
    const fetchClinics = async () => {
      if (process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true') {
        try {
          setIsLoadingTenants(true);
          const activeTenants = await authService.fetchActiveTenants();
          setTenants(activeTenants || []);
          
          // Auto-select first tenant if only one exists
          if (activeTenants && activeTenants.length === 1) {
            setFormData(prev => ({
              ...prev,
              tenantId: activeTenants[0]._id
            }));
          }
        } catch (error) {
          console.error('Error fetching clinics:', error);
        } finally {
          setIsLoadingTenants(false);
        }
      }
    };
    
    fetchClinics();
  }, []);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])/.test(password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Validate tenant selection if multi-tenant is enabled
    if (process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true' && tenants.length > 0 && !tenantId) {
      newErrors.tenantId = 'Please select a clinic';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      // Create the registration data to send to the server
      const registrationData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password: password,
        confirmPassword: confirmPassword,
        role: 'doctor',
        // Add default values for required backend fields
        specialty: 'Pending', // Temporary value
        experience: 'To be determined' // Temporary value
      };
      
      // Add tenant ID if multi-tenant is enabled
      if (process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true' && tenantId) {
        registrationData.tenantId = tenantId;
      }
      
      console.log('Navigating to terms with data:', registrationData);
      
      // Navigate to Terms of Use first (not register directly)
      navigate('/terms-of-use', { 
        state: { 
          registrationData: registrationData,
          tenantId: tenantId 
        }
      });
      
    } catch (error) {
      console.error('Navigation error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }
      
      // Include tenantId for Google auth if multi-tenant is enabled
      const payload = process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true' && tenantId
        ? { credential: credentialResponse.credential, tenantId }
        : credentialResponse.credential;
      
      const response = await authService.googleAuth(payload);
      
      localStorage.setItem('token', response.token);
      
      // Store tenant info if provided
      if (response.tenant) {
        localStorage.setItem('tenant', JSON.stringify(response.tenant));
      }
      
      // Navigate to doctor onboarding instead of regular dashboard
      navigate('/doctor-onboarding');
      
    } catch (error) {
      console.error('Google auth error:', error);
      alert(error.response?.data?.message || 'Google authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleFailure = (error) => {
    console.error('Google sign-in error:', error);
    alert('Google sign-in was unsuccessful. Please try again.');
  };
  
  // Check if multi-tenant is enabled and we have tenants
  const showTenantSelector = process.env.REACT_APP_ENABLE_MULTI_TENANT === 'true' && tenants.length > 0;
  
  return (
    <AuthLayout>

      <form className="auth-form" onSubmit={handleSubmit}>
        
        {/* Clinic selection dropdown - only show if multi-tenant is enabled */}
        {showTenantSelector && (
          <div className="form-group">
            <label htmlFor="tenantId">Select Clinic</label>
            <select
              id="tenantId"
              name="tenantId"
              value={tenantId}
              onChange={handleChange}
              className={errors.tenantId ? 'error' : ''}
              disabled={loading || isLoadingTenants}
            >
              <option value="">-- Select Clinic --</option>
              {tenants.map(tenant => (
                <option key={tenant._id} value={tenant._id}>
                  {tenant.name}
                </option>
              ))}
            </select>
            {errors.tenantId && <span className="error-message">{errors.tenantId}</span>}
          </div>
        )}
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              placeholder="Enter First Name"
              value={firstName}
              onChange={handleChange}
              className={errors.firstName ? 'error' : ''}
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              placeholder="Enter Last Name"
              value={lastName}
              onChange={handleChange}
              className={errors.lastName ? 'error' : ''}
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Enter Email Address"
            value={email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>
        
        <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter Password"
                  value={password}
                  onChange={handleChange}
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  <img src={showPassword ? eyeOpen : eyeClosed} alt="Toggle password visibility" />
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
          
          {/* Password strength indicator */}
          {password && (
            <div className="password-strength">
              <div className={`strength-bar ${
                password.length >= 8 && 
                /[A-Z]/.test(password) && 
                /[a-z]/.test(password) && 
                /[0-9]/.test(password) && 
                /[^A-Za-z0-9]/.test(password) 
                  ? 'strong' 
                  : password.length >= 6 ? 'medium' : 'weak'
              }`}></div>
              <span>{
                password.length >= 8 && 
                /[A-Z]/.test(password) && 
                /[a-z]/.test(password) && 
                /[0-9]/.test(password) && 
                /[^A-Za-z0-9]/.test(password) 
                  ? 'Strong' 
                  : password.length >= 6 ? 'Medium' : 'Weak'
              }</span>
            </div>
          )}
        </div>
        
       <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
            >
              <img src={showConfirmPassword ? eyeOpen : eyeClosed} alt="Toggle password visibility" />
            </button>
          </div>
          {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
        </div>
        
        <button type="submit" className="auth-button" disabled={loading}>
          {loading ? 'Processing...' : 'Create Professional Account'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default Register;